import React, { useState, useEffect, useMemo, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Search, Eye, Clock, CheckCircle, XCircle, Truck, ChefHat, Package, ChevronLeft, ChevronRight, RefreshCw, X, TrendingUp, BarChart3 } from 'lucide-react'
import { api } from '../../../lib/api'
import { useStore } from '../../../contexts/StoreContext'
import { useAuth } from '../../../contexts/AuthContext'
import toast from 'react-hot-toast'
import ProductImage from '../../../components/ProductImage'
import { getCache, getCacheStale, setCache } from '../../../lib/cache'
import { showOfflineToast } from '../../../lib/toastManager'

interface OrderItem {
  id: string
  quantity: number
  unit_price: number
  product: {
    id: string
    name: string
    image_url?: string
  }
}

interface Order {
  id: string
  store_id: string
  customer_name: string
  customer_phone: string
  status: 'pending' | 'preparing' | 'ready' | 'delivering' | 'delivered' | 'cancelled'
  total_amount: number
  delivery_fee: number
  tax_amount?: number
  estimated_delivery_time?: number
  assigned_delivery_id?: string
  created_at: string
  updated_at: string
  items: OrderItem[]
}

interface Employee {
  id: string
  name: string
  email: string
  type: 'chef' | 'delivery' | 'dispatcher' | 'store_admin'
  is_active: boolean
}

type OrderStatus = Order['status']

interface StatusConfig {
  value: OrderStatus
  label: string
  icon: React.ReactElement
  color: string
  bgColor: string
  borderColor: string
  textColor: string
}

const StoreOrders = () => {
  const navigate = useNavigate()
  const { storeId } = useParams<{ storeId: string }>()
  const { activeStore } = useStore()
  const { user } = useAuth()
  
  // Redirigir despachadores a su dashboard
  useEffect(() => {
    if (user?.type === 'dispatcher') {
      const userStoreId = user.storeId
      if (userStoreId) {
        navigate(`/dispatcher`)
      } else {
        navigate('/dispatcher')
      }
      return
    }
  }, [user, navigate])
  
  const [allOrders, setAllOrders] = useState<Order[]>([]) // Todas las órdenes para estadísticas
  const [orders, setOrders] = useState<Order[]>([]) // Órdenes filtradas
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterStatus, setFilterStatus] = useState<OrderStatus>('pending') // Cambiar de 'all' a 'pending'
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [updatingStatus, setUpdatingStatus] = useState<string | null>(null)
  const [deliveryDrivers, setDeliveryDrivers] = useState<Employee[]>([])
  const [showAssignDeliveryModal, setShowAssignDeliveryModal] = useState(false)
  const [selectedOrderForDelivery, setSelectedOrderForDelivery] = useState<Order | null>(null)
  const [assigningDelivery, setAssigningDelivery] = useState(false)
  const [isHeaderVisible, setIsHeaderVisible] = useState(true)
  const [lastScrollY, setLastScrollY] = useState(0)
  const ordersPerPage = 10

  // Refs para prevenir duplicados en React StrictMode
  const loadingOrdersRef = useRef(false)
  const loadingAllOrdersRef = useRef(false)
  const loadingDriversRef = useRef(false)

  // Hook para ocultar/mostrar header en scroll (solo pantallas pequeñas)
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY
      const isSmallScreen = window.innerWidth < 640

      if (!isSmallScreen) {
        setIsHeaderVisible(true)
        return
      }

      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setIsHeaderVisible(false)
      } else if (currentScrollY < lastScrollY) {
        setIsHeaderVisible(true)
      }

      setLastScrollY(currentScrollY)
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [lastScrollY])

  const currentStoreId = storeId || activeStore?.id

  // Configuración de estados en español
  const statusConfigs: StatusConfig[] = [
    {
      value: 'pending',
      label: 'Pendientes',
      icon: <Clock className="w-4 h-4 sm:w-5 sm:h-5" />,
      color: 'yellow',
      bgColor: 'bg-gradient-to-br from-yellow-50 to-yellow-100',
      borderColor: 'border-yellow-300',
      textColor: 'text-yellow-800'
    },
    {
      value: 'preparing',
      label: 'Preparando',
      icon: <ChefHat className="w-4 h-4 sm:w-5 sm:h-5" />,
      color: 'blue',
      bgColor: 'bg-gradient-to-br from-blue-50 to-blue-100',
      borderColor: 'border-blue-300',
      textColor: 'text-blue-800'
    },
    {
      value: 'ready',
      label: 'Listos',
      icon: <Package className="w-4 h-4 sm:w-5 sm:h-5" />,
      color: 'purple',
      bgColor: 'bg-gradient-to-br from-purple-50 to-purple-100',
      borderColor: 'border-purple-300',
      textColor: 'text-purple-800'
    },
    {
      value: 'delivering',
      label: 'En Camino',
      icon: <Truck className="w-4 h-4 sm:w-5 sm:h-5" />,
      color: 'orange',
      bgColor: 'bg-gradient-to-br from-orange-50 to-orange-100',
      borderColor: 'border-orange-300',
      textColor: 'text-orange-800'
    },
    {
      value: 'delivered',
      label: 'Entregados',
      icon: <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5" />,
      color: 'green',
      bgColor: 'bg-gradient-to-br from-green-50 to-green-100',
      borderColor: 'border-green-300',
      textColor: 'text-green-800'
    },
    {
      value: 'cancelled',
      label: 'Cancelados',
      icon: <XCircle className="w-4 h-4 sm:w-5 sm:h-5" />,
      color: 'red',
      bgColor: 'bg-gradient-to-br from-red-50 to-red-100',
      borderColor: 'border-red-300',
      textColor: 'text-red-800'
    }
  ]

  // Calcular estadísticas por estado
  const statusStats = useMemo(() => {
    const stats: Record<OrderStatus, number> = {
      pending: 0,
      preparing: 0,
      ready: 0,
      delivering: 0,
      delivered: 0,
      cancelled: 0
    }
    
    allOrders.forEach(order => {
      stats[order.status] = (stats[order.status] || 0) + 1
    })
    
    return stats
  }, [allOrders])

  useEffect(() => {
    if (currentStoreId) {
      if (!loadingAllOrdersRef.current) {
        loadingAllOrdersRef.current = true
        loadAllOrders() // Cargar todas las órdenes para estadísticas
      }
      if (!loadingOrdersRef.current) {
        loadingOrdersRef.current = true
        loadOrders() // Cargar órdenes filtradas
      }
      if (!loadingDriversRef.current) {
        loadingDriversRef.current = true
        loadDeliveryDrivers()
      }
    }
    
    return () => {
      loadingAllOrdersRef.current = false
      loadingOrdersRef.current = false
      loadingDriversRef.current = false
    }
  }, [currentStoreId, currentPage, filterStatus])

  // Cargar todas las órdenes para estadísticas (sin paginación)
  const loadAllOrders = async () => {
    if (!currentStoreId) return

    const cacheKey = `store_orders_all_${currentStoreId}`
    
    const cachedOrders = getCacheStale<Order[]>(cacheKey)
    if (cachedOrders && cachedOrders.length > 0) {
      setAllOrders(cachedOrders)
      // Si tenemos datos válidos en cache, hacer petición en background para actualizar
      // pero no bloquear la UI
    }

    try {
      const params = new URLSearchParams({
        page: '1',
        limit: '1000' // Cargar muchas órdenes para estadísticas
      })

      // Usar timeout más largo para consultas de pedidos (pueden ser pesadas)
      const response = await api.get(`/orders/store/${currentStoreId}?${params}`, {
        timeout: 60000 // 60 segundos para consultas de pedidos
      })
      if (response.data.success) {
        const normalizedOrders = (response.data.data || []).map((order: any) => ({
          id: order.id,
          store_id: order.store_id || order.storeId,
          customer_name: order.customer_name || order.customerName,
          customer_phone: order.customer_phone || order.customerPhone,
          status: order.status,
          total_amount: Number(order.total_amount || order.totalAmount) || 0,
          delivery_fee: Number(order.delivery_fee || order.deliveryFee) || 0,
          tax_amount: Number(order.tax_amount || order.taxAmount) || 0,
          estimated_delivery_time: Number(order.estimated_delivery_time || order.estimatedDeliveryTime) || 0,
          assigned_delivery_id: order.assigned_delivery_id || order.assignedDeliveryId,
          created_at: order.created_at || order.createdAt,
          updated_at: order.updated_at || order.updatedAt,
          items: (order.items || []).map((item: any) => ({
            id: item.id,
            quantity: Number(item.quantity) || 0,
            unit_price: Number(item.unit_price || item.unitPrice) || 0,
            product: {
              id: item.product?.id || item.product_id || '',
              name: item.product?.name || 'Producto',
              image_url: item.product?.image_url || item.product?.imageUrl
            }
          }))
        }))
        setAllOrders(normalizedOrders)
        setCache(cacheKey, normalizedOrders, 2 * 60 * 1000)
      }
    } catch (error: any) {
      console.error('Error loading all orders:', error)
      // Si hay timeout o error de red y tenemos cache, usar cache sin mostrar error
      if ((error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED' || !error.response) && cachedOrders) {
        showOfflineToast()
        return
      }
      // Solo loguear error si no tenemos cache
      if (!cachedOrders || cachedOrders.length === 0) {
        console.warn('No hay datos en caché para mostrar')
      }
    } finally {
      loadingAllOrdersRef.current = false
    }
  }

  const loadOrders = async () => {
    if (!currentStoreId) return

    const cacheKey = `store_orders_${currentStoreId}_${currentPage}_${filterStatus}`
    
    const cachedOrders = getCacheStale<Order[]>(cacheKey)
    if (cachedOrders && cachedOrders.length > 0) {
      setOrders(cachedOrders)
      setLoading(false)
      // Si tenemos datos válidos en cache, hacer petición en background para actualizar
      // pero no bloquear la UI
    }

    try {
      setLoading(true)
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: ordersPerPage.toString(),
        status: filterStatus // Siempre filtrar por estado (ya no hay 'all')
      })

      // Usar timeout más largo para consultas de pedidos (pueden ser pesadas)
      const response = await api.get(`/orders/store/${currentStoreId}?${params}`, {
        timeout: 60000 // 60 segundos para consultas de pedidos
      })
      if (response.data.success) {
        const normalizedOrders = (response.data.data || []).map((order: any) => ({
          id: order.id,
          store_id: order.store_id || order.storeId,
          customer_name: order.customer_name || order.customerName,
          customer_phone: order.customer_phone || order.customerPhone,
          status: order.status,
          total_amount: Number(order.total_amount || order.totalAmount) || 0,
          delivery_fee: Number(order.delivery_fee || order.deliveryFee) || 0,
          tax_amount: Number(order.tax_amount || order.taxAmount) || 0,
          estimated_delivery_time: Number(order.estimated_delivery_time || order.estimatedDeliveryTime) || 0,
          assigned_delivery_id: order.assigned_delivery_id || order.assignedDeliveryId,
          created_at: order.created_at || order.createdAt,
          updated_at: order.updated_at || order.updatedAt,
          items: (order.items || []).map((item: any) => ({
            id: item.id,
            quantity: Number(item.quantity) || 0,
            unit_price: Number(item.unit_price || item.unitPrice) || 0,
            product: {
              id: item.product?.id || item.product_id || '',
              name: item.product?.name || 'Producto',
              image_url: item.product?.image_url || item.product?.imageUrl
            }
          }))
        }))
        setOrders(normalizedOrders)
        setTotalPages(response.data.pagination?.totalPages || 1)
        
        setCache(cacheKey, normalizedOrders, 2 * 60 * 1000)
      }
    } catch (error: any) {
      console.error('Error loading orders:', error)
      
      // Si hay timeout o error de red y tenemos cache, usar cache
      if ((error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED' || !error.response) && cachedOrders) {
        showOfflineToast()
        return
      }
      
      // Solo mostrar error si no tenemos cache
      if (!cachedOrders || cachedOrders.length === 0) {
        if (error.code === 'ECONNABORTED') {
          toast.error('La consulta está tardando demasiado. Intenta recargar la página.')
        }
      }
      
      // Solo mostrar error si no hay datos en cache
      if (!cachedOrders || cachedOrders.length === 0) {
        toast.error(error.response?.data?.message || 'Error al cargar pedidos')
        setOrders([])
      }
    } finally {
      setLoading(false)
      setRefreshing(false)
      loadingOrdersRef.current = false
    }
  }

  const loadDeliveryDrivers = async () => {
    if (!currentStoreId) return

    const cacheKey = `delivery_drivers_${currentStoreId}`
    
    const cachedDrivers = getCacheStale<Employee[]>(cacheKey)
    if (cachedDrivers && cachedDrivers.length > 0) {
      setDeliveryDrivers(cachedDrivers)
      // Si tenemos datos válidos en cache, no hacer petición HTTP
      loadingDriversRef.current = false
      return
    }

    try {
      const response = await api.get(`/auth/store/${currentStoreId}/employees`)
      if (response.data.success) {
        const employees = response.data.data || []
        const drivers = employees.filter((e: Employee) => e.type === 'delivery' && e.is_active)
        setDeliveryDrivers(drivers)
        
        setCache(cacheKey, drivers, 10 * 60 * 1000)
      }
    } catch (error: any) {
      console.error('Error loading delivery drivers:', error)
      
      if ((error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED' || !error.response) && cachedDrivers) {
        return
      }
    } finally {
      loadingDriversRef.current = false
    }
  }

  const handleAssignDelivery = async (order: Order, driverId: string) => {
    try {
      setAssigningDelivery(true)
      const response = await api.patch(`/orders/${order.id}/status`, {
        status: order.status,
        assignedDeliveryId: driverId
      })
      
      if (response.data.success) {
        toast.success('Repartidor asignado exitosamente')
        setShowAssignDeliveryModal(false)
        setSelectedOrderForDelivery(null)
        await loadOrders()
        await loadAllOrders() // Actualizar estadísticas
      } else {
        toast.error(response.data.message || 'Error al asignar repartidor')
      }
    } catch (error: any) {
      console.error('Error assigning delivery:', error)
      toast.error(error.response?.data?.message || 'Error al asignar repartidor')
    } finally {
      setAssigningDelivery(false)
    }
  }

  const handleRefresh = async () => {
    setRefreshing(true)
    await Promise.all([loadOrders(), loadAllOrders()])
  }

  const filteredOrders = orders.filter(order =>
    order.customer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    order.customer_phone.includes(searchTerm) ||
    order.id.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const handleStatusChange = async (orderId: string, newStatus: OrderStatus) => {
    const order = orders.find(o => o.id === orderId)
    if (newStatus === 'delivering' && !order?.assigned_delivery_id) {
      toast.error('No se puede marcar como "En Camino" sin asignar un repartidor')
      return
    }

    if (!confirm(`¿Estás seguro de cambiar el estado a "${getStatusLabel(newStatus)}"?`)) {
      return
    }

    try {
      setUpdatingStatus(orderId)
      const response = await api.patch(`/orders/${orderId}/status`, { status: newStatus })
      if (response.data.success) {
        toast.success(`Estado actualizado a "${getStatusLabel(newStatus)}"`)
        await Promise.all([loadOrders(), loadAllOrders()]) // Actualizar ambas listas
      }
    } catch (error: any) {
      console.error('Error updating order status:', error)
      toast.error(error.response?.data?.message || 'Error al actualizar el estado del pedido')
    } finally {
      setUpdatingStatus(null)
    }
  }

  const getStatusColor = (status: OrderStatus) => {
    const config = statusConfigs.find(s => s.value === status)
    if (config) {
      return `${config.bgColor} ${config.textColor} ${config.borderColor}`
    }
    return 'bg-gray-100 text-gray-800 border-gray-200'
  }

  const getStatusLabel = (status: OrderStatus) => {
    const config = statusConfigs.find(s => s.value === status)
    return config?.label || status
  }

  const getStatusIcon = (status: OrderStatus): React.ReactElement => {
    const config = statusConfigs.find(s => s.value === status)
    return config?.icon || <Clock className="w-4 h-4" />
  }

  const getNextStatuses = (currentStatus: OrderStatus): OrderStatus[] => {
    const isPlatformAdmin = user?.type === 'platform_admin'
    
    switch (currentStatus) {
      case 'pending': 
        return ['preparing', 'cancelled'] as OrderStatus[]
      case 'preparing': 
        return isPlatformAdmin 
          ? ['ready', 'cancelled', 'pending'] as OrderStatus[]
          : ['ready', 'cancelled'] as OrderStatus[]
      case 'ready': 
        return isPlatformAdmin
          ? ['delivering', 'cancelled', 'preparing', 'pending'] as OrderStatus[]
          : ['delivering', 'cancelled'] as OrderStatus[]
      case 'delivering': 
        return isPlatformAdmin
          ? ['delivered', 'ready', 'preparing', 'pending', 'cancelled'] as OrderStatus[]
          : ['delivered'] as OrderStatus[]
      case 'delivered': 
        return isPlatformAdmin 
          ? ['delivering', 'ready', 'preparing', 'pending', 'cancelled'] as OrderStatus[]
          : []
      case 'cancelled': 
        return isPlatformAdmin 
          ? ['pending', 'preparing', 'ready'] as OrderStatus[]
          : []
      default: 
        return []
    }
  }

  if (!currentStoreId) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/40 flex items-center justify-center pb-20">
        <div className="text-center px-4">
          <div className="bg-white/70 backdrop-blur-2xl rounded-xl sm:rounded-2xl shadow-2xl border border-white/20 p-12 max-w-lg mx-auto">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gray-100/80 rounded-xl sm:rounded-2xl mb-6">
              <Package className="w-10 h-10 text-gray-400" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-3">No hay negocio seleccionado</h3>
            <p className="text-gray-600 font-medium">Selecciona un negocio para ver sus pedidos</p>
          </div>
        </div>
      </div>
    )
  }

  if (loading && orders.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/40 flex items-center justify-center pb-20">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-white/60 backdrop-blur-xl rounded-xl sm:rounded-2xl shadow-xl mb-6">
            <div className="animate-spin rounded-full h-8 w-8 border-4 border-blue-200 border-t-blue-600"></div>
          </div>
          <p className="text-gray-600 font-semibold">Cargando pedidos...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/40 pb-20 sm:pb-8">
      {/* Header - Glassmorphism Style - Ocultable en scroll en pantallas pequeñas */}
      <div className={`bg-white/40 backdrop-blur-2xl border-b border-white/20 shadow-lg sticky top-0 z-50 transition-transform duration-300 ${
        isHeaderVisible ? 'translate-y-0' : '-translate-y-full'
      }`}>
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="py-3 sm:py-6 lg:py-8">
            <div className="flex items-center justify-between mb-4 sm:mb-6 lg:mb-8">
              <div className="flex items-center gap-2 sm:gap-4">
                <button
                  onClick={() => navigate(-1)}
                  className="p-2.5 sm:p-3 rounded-xl sm:rounded-2xl bg-white/60 backdrop-blur-md hover:bg-white/80 border border-white/30 shadow-md hover:shadow-lg transition-all duration-200 active:scale-95"
                >
                  <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5 text-gray-700" />
                </button>
                <div className="min-w-0">
                  <h1 className="text-lg sm:text-2xl lg:text-3xl xl:text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent mb-1 sm:mb-2 truncate">
                    Gestión de Pedidos
                  </h1>
                  <p className="text-xs sm:text-sm text-gray-600 font-semibold hidden sm:block">Administra y rastrea todos tus pedidos</p>
                </div>
              </div>
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="p-2.5 sm:p-3 rounded-xl sm:rounded-2xl bg-white/60 backdrop-blur-md hover:bg-white/80 border border-white/30 shadow-md hover:shadow-lg transition-all duration-200 active:scale-95 flex-shrink-0"
              >
                <RefreshCw className={`w-4 h-4 sm:w-5 sm:h-5 text-gray-700 ${refreshing ? 'animate-spin' : ''}`} />
              </button>
            </div>

            {/* Estadísticas por Estado - Glass Cards - Compacto */}
            <div className="grid grid-cols-3 sm:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-3 lg:gap-4">
              {statusConfigs.map((config) => (
                <button
                  key={config.value}
                  onClick={() => {
                    setFilterStatus(config.value)
                    setCurrentPage(1)
                  }}
                  className={`group bg-white/60 backdrop-blur-xl border rounded-xl sm:rounded-2xl p-3 sm:p-4 lg:p-6 cursor-pointer transition-all duration-500 hover:shadow-xl sm:hover:shadow-2xl hover:-translate-y-1 sm:hover:-translate-y-2 active:scale-95 ${
                    filterStatus === config.value 
                      ? 'border-blue-300/50 bg-white/80 shadow-lg sm:shadow-xl ring-2 ring-blue-500/30' 
                      : 'border-white/20 hover:bg-white/80'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2 sm:mb-3">
                    <div className={`${config.textColor} group-hover:scale-110 transition-transform duration-300`}>
                      {React.isValidElement(config.icon) ? React.cloneElement(config.icon as React.ReactElement<{ className?: string }>, { className: 'w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6' }) : config.icon}
                    </div>
                    <span className={`text-lg sm:text-xl lg:text-2xl xl:text-3xl font-bold ${config.textColor}`}>
                      {statusStats[config.value] || 0}
                    </span>
                  </div>
                  <p className={`text-[10px] sm:text-xs font-bold ${config.textColor} uppercase tracking-wider leading-tight`}>
                    {config.label}
                  </p>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8 lg:py-12">
        {/* Search Bar - Glass Style - Compacto */}
        <div className="mb-4 sm:mb-6 lg:mb-8">
          <div className="bg-white/60 backdrop-blur-xl rounded-xl sm:rounded-2xl shadow-lg sm:shadow-xl border border-white/20 p-4 sm:p-6">
            <div className="relative">
              <Search className="absolute left-3 sm:left-5 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5" />
              <input
                type="text"
                placeholder="Buscar por cliente, teléfono o ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 sm:pl-14 pr-10 sm:pr-12 py-2.5 sm:py-4 bg-white/60 backdrop-blur-sm border border-white/30 rounded-xl sm:rounded-2xl focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 focus:bg-white/80 transition-all duration-200 text-sm sm:text-base font-semibold shadow-lg hover:shadow-xl"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 sm:right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors active:scale-95 p-1.5 sm:p-2"
                >
                  <X className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Orders List - Glass Style - Compacto */}
        {filteredOrders.length === 0 ? (
          <div className="bg-white/70 backdrop-blur-2xl rounded-xl sm:rounded-2xl shadow-2xl border border-white/20 p-6 sm:p-10 lg:p-12 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 bg-gradient-to-br from-gray-100 to-gray-200 rounded-xl sm:rounded-2xl mb-4 sm:mb-6 lg:mb-8 shadow-xl">
              <Package className="w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 text-gray-400" />
            </div>
            <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 mb-2 sm:mb-4">No se encontraron pedidos</h3>
            <p className="text-sm sm:text-base text-gray-600 font-medium">
              {searchTerm 
                ? 'Intenta ajustar los términos de búsqueda' 
                : `No hay pedidos con estado "${getStatusLabel(filterStatus)}"`}
            </p>
          </div>
        ) : (
          <div className="space-y-3 sm:space-y-4 lg:space-y-6">
            {filteredOrders.map((order) => (
              <div key={order.id} className="bg-white/60 backdrop-blur-xl rounded-xl sm:rounded-2xl shadow-lg sm:shadow-xl border border-white/20 hover:shadow-xl sm:hover:shadow-2xl hover:-translate-y-1 sm:hover:-translate-y-2 transition-all duration-500 hover:bg-white/80 overflow-hidden">
                <div className="p-4 sm:p-6 lg:p-8">
                  {/* Order Header */}
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4 mb-4 sm:mb-6">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg sm:text-xl lg:text-2xl xl:text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent mb-1 sm:mb-2">
                        Pedido #{order.id.slice(-8)}
                      </h3>
                      <p className="text-xs sm:text-sm lg:text-base text-gray-600 font-medium truncate">
                        {order.customer_name} • {order.customer_phone}
                      </p>
                    </div>
                    <div className="text-left sm:text-right w-full sm:w-auto flex items-center justify-between sm:flex-col sm:items-end gap-2 sm:gap-3">
                      <span className={`inline-flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-xl sm:rounded-2xl text-[10px] sm:text-xs font-bold border backdrop-blur-sm ${getStatusColor(order.status)}`}>
                        {React.isValidElement(getStatusIcon(order.status)) ? React.cloneElement(getStatusIcon(order.status) as React.ReactElement<{ className?: string }>, { className: 'w-3 h-3 sm:w-4 sm:h-4' }) : getStatusIcon(order.status)}
                        <span>{getStatusLabel(order.status)}</span>
                      </span>
                      <p className="text-lg sm:text-xl lg:text-2xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                        RD${((Number(order.total_amount) || 0) + (Number(order.delivery_fee) || 0)).toFixed(2)}
                      </p>
                    </div>
                  </div>

                  {/* Order Items - Compacto */}
                  <div className="mb-4 sm:mb-6">
                    <p className="text-xs sm:text-sm font-bold text-gray-700 mb-2 sm:mb-4 uppercase tracking-wider">Productos:</p>
                    <div className="space-y-2 sm:space-y-3">
                      {order.items.slice(0, 2).map((item) => (
                        <div key={item.id} className="flex items-center gap-2 sm:gap-4 p-2 sm:p-4 bg-white/40 backdrop-blur-sm rounded-xl sm:rounded-2xl border border-white/30 hover:bg-white/60 transition-all duration-300">
                          <div className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 flex-shrink-0 bg-white rounded-xl sm:rounded-2xl overflow-hidden shadow-lg">
                            <ProductImage
                              imageKey={item.product.image_url || null}
                              alt={item.product.name}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs sm:text-sm lg:text-base font-bold text-gray-900 truncate">{item.product.name}</p>
                            <p className="text-[10px] sm:text-xs lg:text-sm text-gray-600 font-medium">Cant: {item.quantity}</p>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <p className="text-sm sm:text-base lg:text-lg font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                              RD${((Number(item.quantity) || 0) * (Number(item.unit_price) || 0)).toFixed(2)}
                            </p>
                          </div>
                        </div>
                      ))}
                      {order.items.length > 2 && (
                        <p className="text-xs sm:text-sm text-gray-500 text-center py-2 sm:py-3 font-semibold">
                          +{order.items.length - 2} productos más
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Order Meta - Compacto */}
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 sm:gap-0 text-xs sm:text-sm text-gray-500 mb-4 sm:mb-6 pb-4 sm:pb-6 border-b border-white/30">
                    <span className="font-medium">{new Date(order.created_at).toLocaleString('es-ES', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}</span>
                    {order.estimated_delivery_time && order.status !== 'delivered' && order.status !== 'cancelled' && (
                      <span className="bg-blue-50/80 text-blue-700 px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg sm:rounded-xl text-xs sm:text-sm font-semibold border border-blue-200/50 backdrop-blur-sm">
                        ~{order.estimated_delivery_time} min
                      </span>
                    )}
                  </div>

                  {/* Action Buttons - Compacto */}
                  <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                    <button
                      onClick={() => navigate(`/store/${currentStoreId}/orders/${order.id}`)}
                      className="flex-1 flex items-center justify-center gap-1.5 sm:gap-2 px-4 sm:px-6 py-2.5 sm:py-4 bg-white/60 backdrop-blur-sm border border-white/30 rounded-xl sm:rounded-2xl text-xs sm:text-sm font-bold text-gray-700 hover:bg-white/80 hover:shadow-xl transition-all duration-300 active:scale-95"
                    >
                      <Eye className="w-4 h-4 sm:w-5 sm:h-5" />
                      <span>Ver Detalles</span>
                    </button>

                    {/* Assign Delivery Button */}
                    {order.status === 'ready' && user && ['store_owner', 'store_admin', 'dispatcher'].includes(user.type) && (
                      <button
                        onClick={() => {
                          setSelectedOrderForDelivery(order)
                          setShowAssignDeliveryModal(true)
                        }}
                        disabled={deliveryDrivers.length === 0}
                        className="flex items-center justify-center gap-1.5 sm:gap-2 px-4 sm:px-6 py-2.5 sm:py-4 bg-gradient-to-r from-orange-600 to-orange-700 hover:from-orange-700 hover:to-orange-800 text-white rounded-xl sm:rounded-2xl text-xs sm:text-sm font-bold transition-all duration-300 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed shadow-xl hover:shadow-2xl"
                      >
                        <Truck className="w-4 h-4 sm:w-5 sm:h-5" />
                        <span className="hidden sm:inline">{order.assigned_delivery_id ? 'Cambiar Repartidor' : deliveryDrivers.length === 0 ? 'Sin Repartidores' : 'Asignar Repartidor'}</span>
                        <span className="sm:hidden">{order.assigned_delivery_id ? 'Cambiar' : 'Asignar'}</span>
                      </button>
                    )}

                    {/* Status Change Buttons */}
                    {getNextStatuses(order.status).length > 0 && (
                      <div className="flex flex-wrap gap-2 sm:gap-3">
                        {getNextStatuses(order.status).map(nextStatus => (
                          <button
                            key={nextStatus}
                            onClick={() => handleStatusChange(order.id, nextStatus)}
                            disabled={updatingStatus === order.id}
                            className={`px-4 sm:px-6 py-2.5 sm:py-4 rounded-xl sm:rounded-2xl text-xs sm:text-sm font-bold transition-all duration-300 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed shadow-xl hover:shadow-2xl ${
                              nextStatus === 'cancelled'
                                ? 'bg-red-50/80 text-red-700 hover:bg-red-100/80 border border-red-200/50 backdrop-blur-sm'
                                : 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700'
                            }`}
                          >
                            {updatingStatus === order.id ? (
                              <span className="flex items-center gap-1.5 sm:gap-2">
                                <RefreshCw className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" />
                                <span className="hidden sm:inline">Actualizando...</span>
                                <span className="sm:hidden">...</span>
                              </span>
                            ) : (
                              <>
                                {nextStatus === 'preparing' && 'Preparar'}
                                {nextStatus === 'ready' && 'Listo'}
                                {nextStatus === 'delivering' && 'Enviar'}
                                {nextStatus === 'delivered' && 'Entregado'}
                                {nextStatus === 'cancelled' && 'Cancelar'}
                              </>
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination - Compacto */}
        {totalPages > 1 && (
          <div className="mt-6 sm:mt-8 lg:mt-10 flex items-center justify-center gap-2 sm:gap-4">
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="p-2.5 sm:p-4 bg-white/60 backdrop-blur-xl border border-white/30 rounded-xl sm:rounded-2xl text-gray-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-white/80 hover:shadow-xl transition-all duration-300 active:scale-95 shadow-lg"
            >
              <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
            <span className="text-xs sm:text-sm font-bold text-gray-700 bg-white/60 backdrop-blur-xl px-4 sm:px-6 py-2.5 sm:py-4 rounded-xl sm:rounded-2xl border border-white/30 shadow-lg">
              {currentPage} de {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              className="p-2.5 sm:p-4 bg-white/60 backdrop-blur-xl border border-white/30 rounded-xl sm:rounded-2xl text-gray-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-white/80 hover:shadow-xl transition-all duration-300 active:scale-95 shadow-lg"
            >
              <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
          </div>
        )}

        {/* Assign Delivery Modal */}
        {showAssignDeliveryModal && selectedOrderForDelivery && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-md z-50 flex items-center justify-center p-4">
            <div className="bg-white/80 backdrop-blur-2xl rounded-xl sm:rounded-2xl max-w-md w-full shadow-2xl border border-white/30">
              <div className="sticky top-0 bg-white/60 backdrop-blur-xl border-b border-white/30 px-6 py-5 flex items-center justify-between rounded-t-[2.5rem]">
                <h3 className="text-2xl font-bold text-gray-900">Asignar Repartidor</h3>
                <button
                  onClick={() => {
                    setShowAssignDeliveryModal(false)
                    setSelectedOrderForDelivery(null)
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors active:scale-95 p-2"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-6">
                {deliveryDrivers.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="inline-flex items-center justify-center w-20 h-20 bg-gray-100/80 rounded-xl sm:rounded-2xl mb-6">
                      <Truck className="w-10 h-10 text-gray-400" />
                    </div>
                    <p className="text-gray-600 mb-2 font-semibold">No hay repartidores disponibles</p>
                    <p className="text-sm text-gray-500 font-medium">Agrega repartidores en la sección de empleados</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {deliveryDrivers.map((driver) => (
                      <button
                        key={driver.id}
                        onClick={() => handleAssignDelivery(selectedOrderForDelivery, driver.id)}
                        disabled={assigningDelivery}
                        className="w-full flex items-center gap-4 p-5 bg-white/60 backdrop-blur-sm hover:bg-white/80 rounded-2xl border border-white/30 transition-all duration-300 disabled:opacity-50 active:scale-95 shadow-lg hover:shadow-xl"
                      >
                        <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg">
                          <Truck className="w-6 h-6 text-white" />
                        </div>
                        <div className="flex-1 text-left">
                          <p className="font-bold text-gray-900">{driver.name}</p>
                          <p className="text-sm text-gray-600 font-medium">{driver.email}</p>
                        </div>
                        {assigningDelivery && (
                          <div className="animate-spin rounded-full h-6 w-6 border-4 border-blue-200 border-t-blue-600"></div>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default StoreOrders
